/**
 * useConversations — fetches and manages the conversation list.
 */

import { useCallback, useEffect, useState } from "react";
import type { ChatAssistant } from "./types.js";

export interface ConversationSummary {
  id: string;
  title: string;
  assistant: ChatAssistant;
  message_count: number;
  last_message_at: string | null;
  updated_at: string;
}

export interface UseConversationsOptions {
  assistant?: ChatAssistant;
  enabled?: boolean;
}

export interface UseConversationsReturn {
  conversations: ConversationSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useConversations({
  assistant,
  enabled = true,
}: UseConversationsOptions = {}): UseConversationsReturn {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConversations = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (assistant) params.set("assistant", assistant);

      const res = await fetch(`/api/intel/chat/conversations?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = (await res.json()) as { conversations: ConversationSummary[] };
      setConversations(data.conversations ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [assistant, enabled]);

  useEffect(() => {
    void fetchConversations();
  }, [fetchConversations]);

  return { conversations, loading, error, refresh: fetchConversations };
}
